import { useMemo } from "react"

interface JsonDiffProps {
  before: unknown
  after: unknown
}

type DiffLine = {
  type: "added" | "removed" | "unchanged"
  content: string
}

function formatJson(value: unknown): string[] {
  try {
    return JSON.stringify(value, null, 2).split("\n")
  } catch {
    return [String(value)]
  }
}

function computeDiff(beforeLines: string[], afterLines: string[]): DiffLine[] {
  const result: DiffLine[] = []

  const beforeSet = new Set(beforeLines)
  const afterSet = new Set(afterLines)

  const removedLines = new Set(beforeLines.filter((l) => !afterSet.has(l)))
  const addedLines = new Set(afterLines.filter((l) => !beforeSet.has(l)))

  let bi = 0
  let ai = 0

  while (bi < beforeLines.length || ai < afterLines.length) {
    const bLine = bi < beforeLines.length ? beforeLines[bi]! : undefined
    const aLine = ai < afterLines.length ? afterLines[ai]! : undefined

    if (bLine !== undefined && aLine !== undefined && bLine === aLine) {
      result.push({ type: "unchanged", content: bLine })
      bi++
      ai++
    } else if (bLine !== undefined && removedLines.has(bLine)) {
      result.push({ type: "removed", content: bLine })
      bi++
    } else if (aLine !== undefined && addedLines.has(aLine)) {
      result.push({ type: "added", content: aLine })
      ai++
    } else {
      if (bLine !== undefined) {
        result.push({ type: "removed", content: bLine })
        bi++
      }
      if (aLine !== undefined) {
        result.push({ type: "added", content: aLine })
        ai++
      }
    }
  }

  return result
}

const lineColors = {
  added: "bg-green-500/10 text-green-400",
  removed: "bg-red-500/10 text-red-400",
  unchanged: "text-zinc-400",
}

const linePrefix = {
  added: "+",
  removed: "-",
  unchanged: " ",
}

export function JsonDiff({ before, after }: JsonDiffProps) {
  const diff = useMemo(() => {
    const beforeLines = formatJson(before)
    const afterLines = formatJson(after)
    return computeDiff(beforeLines, afterLines)
  }, [before, after])

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-auto max-h-[32rem]">
      <pre className="text-[0.8125rem] leading-relaxed p-4 font-mono">
        {diff.map((line, i) => (
          <div key={i} className={`${lineColors[line.type]} px-2 -mx-2`}>
            <span className="inline-block w-4 select-none opacity-60">
              {linePrefix[line.type]}
            </span>
            {line.content}
          </div>
        ))}
      </pre>
    </div>
  )
}
